import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { MutationLifecycleApi } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { dispatch, RootState } from 'store';
import { broadcastTimecardEvent, getTabId } from 'store/middleware/timecardPersistence';
import { resetTimecardState, setStopwatchRunning, trackTimecard } from 'store/reducers/timecard';
import { normalizeExpenseAmounts } from 'types/expenses';
import { NamedEntityMinDto } from 'types/other';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter.ts';
import {
    ImprovedTimecardDescription,
    SwitchTimecardsMatter,
    TimecardCreateUpdateDto,
    TimecardDescriptionRules,
    TimecardDetails,
    TimecardGroupTotalsDto,
    TimecardListExportItemDto,
    TimecardListItemData,
    TimecardMatterHistoryItem,
    TimecardsTotalsDto,
    UpdateTimecardsBillability,
} from 'types/timecards';
import { objectToQueryParams } from 'utils/linkUtil';
import { QueryParams } from './common/types';
import { APIMattersSlice } from './matters';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters, paginatedQueryWithFilters } from './util/endpointBuilder';
import { handleRTKQueryError } from './util/errorHandling';
import { filteredAndSortedQuery } from './util/filteredAndSortedQuery';
import {
    groupKeysToRequest,
    hasNewGroupKeys,
    mergeTimecardGroupTotals,
    TimecardGroupTotalsArgs,
    timecardGroupTotalsCacheKey,
    timecardGroupTotalsFilter,
} from './util/timecardGroupTotals';

const paginationQueryEndpoints = ['getTimecardsWithFilters', 'getPaginatedTimecardsWithFilters'] as const;

const normalizeTimecardDetailsResponse = (response: TimecardDetails): TimecardDetails => ({
    ...response,
    expenses: response.expenses?.map(normalizeExpenseAmounts) ?? response.expenses,
});

// Shared by getTimecardById and the redirect-exempt getTrackedTimecardById so the two fetches of the
// same resource cannot drift.
const timecardByIdConfig = {
    query: (id: number) => `/v1/timecards/${id}`,
    providesTags: (_1: unknown, _2: unknown, arg: number) => [{ type: 'Timecards' as const, id: arg }],
    transformResponse: normalizeTimecardDetailsResponse,
};

/**
 * Manually updates given timecard in list caches
 */
const updateTimecardInLists = async (
    timecard: TimecardDetails,
    api: MutationLifecycleApi<
        number,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        TimecardDetails,
        'portalAPI'
    >
) => {
    const queries = api.getState().portalAPI.queries;
    paginationQueryEndpoints.forEach((endpointName) => {
        const endpointQueries = Object.entries(queries)
            .filter((entry) => entry[1]?.endpointName === endpointName)
            .map((entry) => entry[1]);
        endpointQueries.forEach((query) => {
            api.dispatch(
                APITimecardsSlice.util.updateQueryData(
                    endpointName,
                    query?.originalArgs as PaginationProps,
                    (draft) => {
                        const index = draft.content.findIndex((tc) => tc.id === timecard.id);
                        if (index !== -1) {
                            draft.content[index] = timecard;
                        }
                    }
                )
            );
        });
    });
};

/**
 * Manually updates given timecard in both by-id caches. The tracker reads its own copy (getTrackedTimecardById)
 * and shows it once the stopwatch is paused, so a patch that misses it leaves the tracker on a stale total.
 */
export const updateTimecardByIdCaches = (
    apiDispatch: ThunkDispatch<RootState, unknown, AnyAction>,
    timecardId: number,
    timecard: Partial<TimecardDetails>
) => {
    (['getTimecardById', 'getTrackedTimecardById'] as const).forEach((endpointName) =>
        apiDispatch(
            APITimecardsSlice.util.updateQueryData(endpointName, timecardId, (draft) => {
                if (draft) {
                    Object.assign(draft, timecard);
                }
            })
        )
    );
};

// The blockers ride along: whether the stopwatch runs decides whether a draft without time can be finished.
const stopwatchStateOf = ({
    isStopwatchRunning,
    totalTimeInSeconds,
    stopwatchRunningSince,
    finishBlockers,
}: TimecardDetails): Pick<
    TimecardDetails,
    'isStopwatchRunning' | 'totalTimeInSeconds' | 'stopwatchRunningSince' | 'finishBlockers'
> => ({
    isStopwatchRunning,
    totalTimeInSeconds,
    stopwatchRunningSince,
    finishBlockers,
});

export const APITimecardsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Timecards',
            'TimecardsCount',
            'TimecardsTotals',
            'TrackedTimecard',
            'FilteredAndSortedList',
            'TimeRecords',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getTimecardById: builder.query<TimecardDetails, number>(timecardByIdConfig),
            // Same endpoint/config as getTimecardById, but for the sidebar tracker's speculative fetch
            // of the locally-stored tracked id. Kept out of the global not-found map so that when the
            // tracked timecard is deleted, the list invalidation's forced refetch 404s silently and
            // the tracker recovers locally (useTrackedTimecard resets state) instead of bouncing to /404.
            getTrackedTimecardById: builder.query<TimecardDetails, number>(timecardByIdConfig),
            getTimecardsList: filteredAndSortedQuery<TimecardListItemData>(builder, '/v1/timecards', {
                providesTags: ['FilteredAndSortedList'],
            }),
            getTimecardsCount: injectCountEndPoint(builder, '/v1/timecards', 'TimecardsCount'),
            getTimecardsTotals: builder.query<TimecardsTotalsDto, Pick<PaginationProps, 'filters'>>({
                query: ({ filters }) => `/v1/timecards/totals${objectToQueryParams({ filter: filters ?? '' })}`,
                providesTags: ['TimecardsTotals'],
                keepUnusedDataFor: 0,
            }),
            // The grouped list's ribbons. Same filter as the list and the same `TimecardsTotals` tag, so a
            // billability or matter switch refreshes the ribbons with the totals row above them.
            //
            // One cache entry per filter and grouping: the group keys only say which of its groups the
            // page holds rows of. A fetch runs only once a key nobody asked for yet turns up, asks for
            // the groups the entry has no totals for, and merges the answer into the entry by key — so
            // scrolling into a new group fetches that group alone while the ribbons on screen keep theirs.
            // A forced fetch (tag invalidation) asks for every loaded group, since all of them went stale.
            getTimecardGroupTotals: builder.query<TimecardGroupTotalsDto[], TimecardGroupTotalsArgs>({
                queryFn: async (args, api, _extraOptions, baseQuery) => {
                    const cached = (api.getState() as RootState).portalAPI.queries[
                        timecardGroupTotalsCacheKey('getTimecardGroupTotals', args)
                    ]?.data as TimecardGroupTotalsDto[] | undefined;
                    const groupKeys = groupKeysToRequest(args.groupKeys, cached, api.forced ?? false);
                    if (groupKeys.length === 0) {
                        return { data: [] };
                    }
                    const result = await baseQuery(
                        `/v1/timecards/group-totals${objectToQueryParams({
                            filter: timecardGroupTotalsFilter({ ...args, groupKeys }),
                            groupBy: args.groupBy,
                        })}`
                    );
                    return result.error ? { error: result.error } : { data: result.data as TimecardGroupTotalsDto[] };
                },
                serializeQueryArgs: ({ endpointName, queryArgs }) =>
                    timecardGroupTotalsCacheKey(endpointName, queryArgs),
                merge: (cached, incoming) => mergeTimecardGroupTotals(cached, incoming),
                forceRefetch: ({ currentArg, previousArg }) =>
                    hasNewGroupKeys(currentArg?.groupKeys, previousArg?.groupKeys),
                providesTags: ['TimecardsTotals'],
                keepUnusedDataFor: 0,
            }),
            getLastAddedTimecardPracticeAreas: builder.query<
                NamedEntityMinDto[],
                { employeeId: string; matterId: number }
            >({
                query: ({ employeeId, matterId }) =>
                    `/v1/timecards/last-added-practice-areas?employeeId=${employeeId}&matterId=${matterId}`,
                keepUnusedDataFor: 0,
            }),
            getLastTimecardCreatedAt: builder.query<string, { employeeId: number }>({
                query: ({ employeeId }) => `/v1/timecards/last-created-at?employeeId=${employeeId}`,
                keepUnusedDataFor: 0,
            }),
            getTimecardsWithFilters: accumulatedQueryWithFilters<TimecardListItemData>(builder, '/v1/timecards', {
                providesTags: ['Timecards'],
                keepUnusedDataFor: 0,
                sort: ['timecardDate,desc', 'timecardStatusTypeCode,asc', 'stopwatchRunningSince,desc'],
                dedupeKey: (timecard) => timecard.id,
            }),
            getPaginatedTimecardsWithFilters: paginatedQueryWithFilters<TimecardListItemData>(
                builder,
                '/v1/timecards',
                { providesTags: ['Timecards'], sort: ['timecardDate,desc', 'stopwatchRunningSince,desc'] }
            ),
            getTimecardsExportList: builder.query<TimecardListExportItemDto[], QueryParams>({
                query: ({ filter = 'timecardStatusTypeCode=out=(DRAFT)', sort = '' }) => ({
                    url: `/v1/timecards/export${objectToQueryParams({ filter, sort })}`,
                    method: 'GET',
                }),
            }),
            getMatterTimecardHistory: builder.query<QueryResponse<TimecardMatterHistoryItem>, number>({
                query: (matterId) => `/v1/timecards/matter-history/${matterId}?page=0&size=20`,
                providesTags: ['Timecards'],
            }),
            addTimecard: builder.mutation<TimecardDetails, TimecardCreateUpdateDto>({
                query: (body) => ({
                    url: `/v1/timecards`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Timecards', 'FilteredAndSortedList', 'TimecardsTotals', 'TimeRecords'],
            }),
            improveTimecardDescription: builder.mutation<
                ImprovedTimecardDescription,
                { text: string; matterId: number | null; languageCode: string | null }
            >({
                query: (body) => ({
                    url: `/v1/timecards/improve-description`,
                    method: 'POST',
                    body,
                }),
            }),
            getTimecardDescriptionRules: builder.query<TimecardDescriptionRules, void>({
                query: () => `/v1/timecards/description-rules`,
            }),
            finishTimecard: builder.mutation<
                TimecardDetails,
                TimecardCreateUpdateDto & { userId?: number; tenantId?: string }
            >({
                query: ({ userId: _userId, tenantId: _tenantId, ...body }) => ({
                    url: `/v1/timecards/finish`,
                    method: 'PUT',
                    body,
                }),
                async onQueryStarted(arg, api) {
                    try {
                        await api.queryFulfilled;

                        // If the finished timecard is the currently tracked one, reset the tracking state
                        const state = api.getState() as RootState;
                        const trackedTimecardId = state.timecard.trackedTimecardId;

                        if (arg.id && trackedTimecardId === String(arg.id) && arg.userId && arg.tenantId) {
                            dispatch(resetTimecardState({ userId: arg.userId, tenantId: arg.tenantId }));
                        }
                    } catch (error) {
                        handleRTKQueryError(error, 'finishTimecard', arg);
                    }
                },
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'TrackedTimecard',
                              'Timecards',
                              'TimecardsTotals',
                              'FilteredAndSortedList',
                              'TimeRecords',
                              { type: 'Timecards', id: arg.id },
                          ]
                        : ['Timecards', 'TimecardsTotals', 'FilteredAndSortedList', 'TimeRecords'],
            }),
            finishMultiAuthorTimecards: builder.mutation<
                TimecardDetails[],
                TimecardCreateUpdateDto & { employees: NamedEntityMinDto[] }
            >({
                query: (body) => ({
                    url: `/v1/timecards/finish-multi`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['Timecards', 'FilteredAndSortedList', 'TimecardsTotals', 'TimeRecords'],
            }),
            getCurrentlyTrackedTimecard: builder.query<
                TimecardDetails | null,
                { userId: number; tenantId: string } | void
            >({
                query: () => '/v1/timecards/currently-tracking',
                providesTags: ['TrackedTimecard'],
                async onQueryStarted(arg, api) {
                    try {
                        const { data: timecard } = await api.queryFulfilled;
                        if (timecard?.isStopwatchRunning && arg && 'userId' in arg) {
                            api.dispatch(
                                trackTimecard({
                                    timecardId: timecard.id,
                                    userId: arg.userId,
                                    tenantId: arg.tenantId,
                                    isStopwatchRunning: true,
                                })
                            );
                        }
                    } catch (error: unknown) {
                        handleRTKQueryError(error, 'getCurrentlyTrackedTimecard', arg);
                    }
                },
            }),
            startTrackingTimecard: builder.mutation<TimecardDetails, { id: number; userId: number; tenantId: string }>({
                query: ({ id }) => ({
                    url: `/v1/timecards/start-tracking/${id}`,
                    method: 'PUT',
                }),
                async onQueryStarted({ id: timecardId, userId, tenantId }, api) {
                    // Store previous state for rollback
                    const previousState = (api.getState() as RootState).timecard;

                    // Optimistic update
                    api.dispatch(trackTimecard({ timecardId, userId, tenantId, isStopwatchRunning: true }));
                    const prevTimecardId = Number(previousState.trackedTimecardId);

                    try {
                        const { data: updatedTimecard } = await api.queryFulfilled;
                        updateTimecardByIdCaches(api.dispatch, updatedTimecard.id, stopwatchStateOf(updatedTimecard));
                        // Broadcast with updated time values from server
                        const wasPreviouslyTracking = previousState.trackedTimecardId === String(timecardId);
                        broadcastTimecardEvent({
                            type: wasPreviouslyTracking ? 'TRACKING_RESUMED' : 'TRACKING_STARTED',
                            payload: {
                                timecardId: String(updatedTimecard.id),
                                timestamp: updatedTimecard.stopwatchRunningSince,
                                totalTimeInSeconds: updatedTimecard.totalTimeInSeconds,
                                stopwatchRunningSince: updatedTimecard.stopwatchRunningSince,
                                senderId: getTabId(),
                            },
                        });
                    } catch (error) {
                        // Complete rollback to previous state
                        if (!isNaN(prevTimecardId)) {
                            api.dispatch(
                                trackTimecard({
                                    timecardId: prevTimecardId,
                                    userId,
                                    tenantId,
                                    isStopwatchRunning: previousState.isStopwatchRunning,
                                })
                            );
                        }
                        // Log error for debugging
                        handleRTKQueryError(error, 'startTrackingTimecard', { id: timecardId, userId, tenantId });
                    }
                },
                invalidatesTags: ['TrackedTimecard'],
            }),
            pauseTrackingTimecard: builder.mutation<TimecardDetails, number>({
                query: (id) => ({
                    url: `/v1/timecards/pause-tracking/${id}`,
                    method: 'PUT',
                }),
                async onQueryStarted(_arg, api) {
                    // Store previous state for proper rollback
                    const wasRunning = (api.getState() as RootState).timecard.isStopwatchRunning;

                    api.dispatch(setStopwatchRunning(false));
                    try {
                        const { data: updatedTimecard } = await api.queryFulfilled;
                        updateTimecardByIdCaches(api.dispatch, updatedTimecard.id, stopwatchStateOf(updatedTimecard));
                        // Broadcast with updated time values from server
                        broadcastTimecardEvent({
                            type: 'TRACKING_PAUSED',
                            payload: {
                                timecardId: String(updatedTimecard.id),
                                totalTimeInSeconds: updatedTimecard.totalTimeInSeconds,
                                senderId: getTabId(),
                            },
                        });
                    } catch (error) {
                        // Restore previous running state
                        api.dispatch(setStopwatchRunning(wasRunning));
                        handleRTKQueryError(error, 'pauseTrackingTimecard', _arg);
                    }
                },
                invalidatesTags: ['TrackedTimecard'],
            }),
            // Utility mutation for updating timecard in caches
            updateTimecardCache: builder.mutation<TimecardDetails, number>({
                query: (id) => ({
                    url: `/v1/timecards/${id}`,
                    method: 'GET',
                }),
                transformResponse: normalizeTimecardDetailsResponse,
                async onQueryStarted(_arg, api) {
                    try {
                        const { data: updatedTimecard } = await api.queryFulfilled;
                        updateTimecardInLists(updatedTimecard, api);
                        updateTimecardByIdCaches(api.dispatch, updatedTimecard.id, updatedTimecard);
                        // eslint-disable-next-line no-empty
                    } catch {}
                },
                invalidatesTags: ['TimecardsTotals'],
            }),
            updateTimecard: builder.mutation<TimecardDetails, TimecardCreateUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/timecards/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'TrackedTimecard',
                              'Timecards',
                              'TimecardsTotals',
                              'FilteredAndSortedList',
                              'TimeRecords',
                              { type: 'Timecards', id: arg.id },
                          ]
                        : ['Timecards', 'TimecardsTotals', 'FilteredAndSortedList', 'TimeRecords'],
            }),
            saveFinishedTimecardAsDraft: builder.mutation<TimecardDetails, TimecardCreateUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/timecards/${id}/save-as-draft`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'TrackedTimecard',
                              'Timecards',
                              'TimecardsTotals',
                              'FilteredAndSortedList',
                              'TimeRecords',
                              { type: 'Timecards', id: arg.id },
                          ]
                        : ['Timecards', 'TimecardsTotals', 'FilteredAndSortedList', 'TimeRecords'],
            }),
            switchTimecardsMatter: builder.mutation<void, SwitchTimecardsMatter>({
                query: ({ ...body }) => ({
                    url: `/v1/timecards/switch-matter`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['Timecards', 'TimecardsTotals', 'FilteredAndSortedList', 'TimeRecords'],
                // Both cases' details change: the uninvoiced-billable-work flag is derived from their timecards.
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(
                        APIMattersSlice.util.invalidateTags([
                            { type: 'Matter', id: String(arg.currentMatterId) },
                            { type: 'Matter', id: String(arg.newMatterId) },
                        ])
                    );
                },
            }),
            updateTimecardsBillability: builder.mutation<void, UpdateTimecardsBillability>({
                query: (body) => ({
                    url: `/v1/timecards/billability`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: [
                    'Timecards',
                    'TimecardsCount',
                    'TimecardsTotals',
                    'FilteredAndSortedList',
                    'TimeRecords',
                ],
                // The case's uninvoiced-billable-work flag is derived from its timecards' billability.
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(arg.matterId) }]));
                },
            }),
            deleteTimecard: builder.mutation<boolean, { id: number; matterId?: number }>({
                query: ({ id }) => ({
                    url: `/v1/timecards/${id}`,
                    method: 'DELETE',
                }),
                async onQueryStarted(_arg, api) {
                    await api.queryFulfilled;
                    if (_arg.matterId) {
                        dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(_arg.matterId) }]));
                    }
                },
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'TrackedTimecard',
                              'Timecards',
                              'TimecardsTotals',
                              'FilteredAndSortedList',
                              'TimeRecords',
                              { type: 'Timecards', id: arg.id },
                          ]
                        : ['Timecards', 'TimecardsTotals', 'FilteredAndSortedList', 'TimeRecords'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetTimecardByIdQuery,
    useLazyGetTimecardByIdQuery,
    useGetTrackedTimecardByIdQuery,
    useGetLastAddedTimecardPracticeAreasQuery,
    useGetLastTimecardCreatedAtQuery,
    useGetTimecardsTotalsQuery,
    useGetTimecardGroupTotalsQuery,
    useLazyGetTimecardsCountQuery,
    useLazyGetTimecardsWithFiltersQuery,
    useGetPaginatedTimecardsWithFiltersQuery,
    useAddTimecardMutation,
    useImproveTimecardDescriptionMutation,
    useGetTimecardDescriptionRulesQuery,
    useFinishTimecardMutation,
    useFinishMultiAuthorTimecardsMutation,
    useGetCurrentlyTrackedTimecardQuery,
    useStartTrackingTimecardMutation,
    usePauseTrackingTimecardMutation,
    useUpdateTimecardCacheMutation,
    useUpdateTimecardMutation,
    useSaveFinishedTimecardAsDraftMutation,
    useSwitchTimecardsMatterMutation,
    useUpdateTimecardsBillabilityMutation,
    useDeleteTimecardMutation,
    useGetMatterTimecardHistoryQuery,
} = APITimecardsSlice;
